import React from "react";
import styled from "styled-components";
import { device } from "../../lib/breakpoints";
import { marketingSectionHeading } from "../../styles/marketingSectionTypography";
import ComprehensiveFeatures from "./ComprehensiveFeatures";
import {
  BarChartOutlined,
  ToolOutlined,
  DollarOutlined,
} from "@ant-design/icons";

/**
 * Shared product overview used on Home and as the lead of /platform.
 * Interactive screenshots + investor pillars only (no Scale-upsell framing).
 */
export default function HomeProductOverview() {
  return (
    <>
      <ComprehensiveFeatures />

      <TrinitySection id="product-pillars" aria-labelledby="product-pillars-heading">
        <TrinityContainer>
          <TrinitySectionTitle id="product-pillars-heading">
            Built for Asset Growth and Exit Value
          </TrinitySectionTitle>
          <TrinitySectionSubtitle>
            Verified portfolio signal, Automated Triage &amp; Dispatch, and
            tax-ready exports that feed your Closing Vault - on every plan.
          </TrinitySectionSubtitle>
          <TrinityGrid>
            <TrinityCard>
              <TrinityIconWrap>
                <BarChartOutlined />
              </TrinityIconWrap>
              <TrinityCardTitle>
                Real-Time DSCR &amp;
                <br />
                Portfolio Health
              </TrinityCardTitle>
              <TrinityCardDescription>
                Live DSCR and NOI on your dashboard: investor-grade visibility
                without spreadsheets. Add debt service to unlock coverage. We
                flag DSCR below 1.2 early so you can protect growth and adjust
                before refinancing gets harder.
              </TrinityCardDescription>
            </TrinityCard>
            <TrinityCard>
              <TrinityIconWrap>
                <ToolOutlined />
              </TrinityIconWrap>
              <TrinityCardTitle>Automated Triage &amp; Dispatch</TrinityCardTitle>
              <TrinityCardDescription>
                Leo diagnoses the issue, confirms lease responsibility, and only
                alerts a vendor when necessary. When a bid is accepted, the work
                order is scheduled with one clear status for vendors. You stay
                in control without living in the thread.
              </TrinityCardDescription>
            </TrinityCard>
            <TrinityCard>
              <TrinityIconWrap>
                <DollarOutlined />
              </TrinityIconWrap>
              <TrinityCardTitle>Tax-Ready Schedule E Export</TrinityCardTitle>
              <TrinityCardDescription>
                Verified income and expense tagging. Audit-ready Schedule E and
                summaries in one click. Export for your CPA or your Closing
                Vault. Grow with defensible records, not guesswork.
              </TrinityCardDescription>
            </TrinityCard>
          </TrinityGrid>
        </TrinityContainer>
      </TrinitySection>
    </>
  );
}

const TrinitySection = styled.section`
  padding: 5rem 2rem;
  background: linear-gradient(180deg, #f8f9fa 0%, var(--color-background) 100%);
  @media ${device.mobile} {
    padding: 3.5rem 1rem;
  }
`;
const TrinityContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;
const TrinitySectionTitle = styled.h2`
  ${marketingSectionHeading}
  color: var(--color-text);
  text-align: center;
  margin: 0 0 0.75rem 0;
`;
const TrinitySectionSubtitle = styled.p`
  font-size: 1.125rem;
  color: var(--color-text-light);
  text-align: center;
  margin: 0 0 3rem 0;
  max-width: 560px;
  margin-left: auto;
  margin-right: auto;
`;
const TrinityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
  @media ${device.mobile} {
    gap: 1.5rem;
  }
`;
const TrinityCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid #eee;
  text-align: center;
`;
const TrinityIconWrap = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  color: var(--color-primary, #2c5f2d);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin-bottom: 1.25rem;
  margin-left: auto;
  margin-right: auto;
  .anticon {
    font-size: 1.5rem;
  }
`;
const TrinityCardTitle = styled.h3`
  font-size: 1.35rem;
  font-weight: 600;
  color: var(--color-text);
  margin: 0 0 0.75rem 0;
  font-family: var(--font-primary);
`;
const TrinityCardDescription = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: var(--color-text-light);
  margin: 0;
  text-wrap: pretty;
`;
